import React from 'react';
import { Routes, Route, Link, useParams } from 'react-router-dom';
// import { Helmet } from 'react-helmet-async';  // Temporarily disabled
import { Shell, Rain, ThemePlayer } from './components/Cinema';
import { CHAPTERS, ACTS, SONGS, CHARACTERS, getChapter, getAdjacent } from './data/story';
import AbcPage from './AbcPage';
import LyricsPage from './LyricsPage';
import '@/styles/dark-pan.css';

// Base URL for meta tags
const SITE_URL = 'https://mgn1point6.com';

// Home page
function DarkPanHome() {
  return (
    <Shell>
      {/* Hero Section with Battersea Background */}
      <section className="relative isolate min-h-[calc(100dvh-3.5rem)] overflow-hidden grain" data-testid="dark-pan-home">
        <img
          src="/stills/battersea.jpg"
          alt="Battersea, 1892, rain and coal smoke"
          className="absolute inset-0 size-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b0a09] via-[#0b0a09]/70 to-[#0b0a09]/25" />
        <Rain />

        <div className="relative z-10 mx-auto flex min-h-[calc(100dvh-3.5rem)] max-w-5xl flex-col justify-end px-5 pb-16 pt-24 sm:px-8 sm:pb-20">
          <p className="text-xs tracking-[0.32em] uppercase" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-everlit)' }}>
            World B · London · 1892 · 11:47 PM
          </p>
          <h1 className="mt-4 max-w-xl text-5xl font-medium tracking-[-0.04em] sm:text-7xl" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-bone)' }}>
            The Dark Pan
          </h1>
          <p className="mt-3 text-lg italic sm:text-2xl" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-paper)' }}>
            A cymatic fairy tale
          </p>
          <p className="mt-6 max-w-xl text-base sm:text-lg" style={{ color: 'rgba(201, 191, 174, 0.9)' }}>
            Some songs don't take you to Neverland. Neverland isn't a place you fly to. It's a job site you're sold on. 
            Second star to the right is a night shift.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/dark-pan/story" className="dp-btn dp-btn-primary" data-testid="enter-story-btn">Enter the story</Link>
            <Link to="/dark-pan/song" className="dp-btn dp-btn-ghost" data-testid="hear-tinked-btn">Hear Tinked</Link>
            <Link to="/dark-pan/lyrics" className="dp-btn dp-btn-ghost" data-testid="lyrics-btn">Full Lyrics</Link>
            <Link to="/dark-pan/tones" className="dp-btn dp-btn-everlit" data-testid="six-tones-btn">Sing the six tones</Link>
            <Link to="/dark-pan/abc" className="dp-btn dp-btn-ghost" data-testid="abc-btn">ABC Primer</Link>
          </div>
        </div>
      </section>

      {/* Trailer Video Section - ONLY the promo */}
      <section style={{ background: '#0A0605', padding: '64px 16px' }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <p style={{ marginBottom: 12, fontSize: 12, letterSpacing: '0.28em', textTransform: 'uppercase', color: '#C9A227' }}>Watch the trailer</p>
          <h2 style={{ marginBottom: 16, fontSize: 32, color: '#F3EAD3', fontFamily: 'Playfair Display, serif' }}>The Dark Pan</h2>
          <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: 12, background: '#000' }}>
            <iframe 
              src="https://www.youtube.com/embed/GFvxleKnwJY?rel=0" 
              title="The Dark Pan Trailer" 
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }} 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
              allowFullScreen 
            />
          </div>
          <p style={{ marginTop: 12 }}>
            <a href="https://youtu.be/GFvxleKnwJY" target="_blank" rel="noopener noreferrer" style={{ color: '#C9A227' }}>Watch on YouTube</a>
          </p>
        </div>
      </section>

      <section className="mx-auto grid max-w-6xl gap-10 px-5 py-16 sm:px-8 lg:grid-cols-2 lg:items-center">
        <div>
          <p className="text-xs tracking-[0.28em] uppercase" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-everlit)' }}>
            Two timelines, one story
          </p>
          <h2 className="mt-3 text-3xl sm:text-4xl" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-bone)' }}>
            Disney left. Sin City right. A brass bowl in the middle.
          </h2>
          <p className="mt-5" style={{ color: 'var(--dp-paper)' }}>
            Mary Darling is told not to sing the Welsh cradle of vowels. Singing
            it forward does nothing. Singing it backward opens a door. On the
            other side: an abandoned World's Fair the mudlarks called Neverland,
            a rusted Ferris wheel that is time, and a girl who tinked a world
            into a singing bowl so a boy would never have a broken heart.
          </p>
          <p className="mt-4" style={{ color: 'var(--dp-fog)' }}>
            Lost Boys are not cute. They are the uncounted. Play is work. Work
            is play. Hook is grown Pan. Tink is a verb.
          </p>
        </div>
        <img
          src="/stills/split.jpg"
          alt="Neverland and the match factory split by a brass bowl"
          className="h-72 w-full rounded-xl object-cover sm:h-96"
        />
      </section>

      <section style={{ borderTop: '1px solid rgba(232, 223, 208, 0.1)', borderBottom: '1px solid rgba(232, 223, 208, 0.1)', background: 'var(--dp-iron)' }}>
        <div className="mx-auto grid max-w-6xl gap-8 px-5 py-14 sm:px-8 md:grid-cols-3">
          {[
            {
              k: "The lullaby",
              t: "A-E-I-O-U-Y",
              d: "Six minor tones, seven cents flat. Backward opens. Forward closes only when the wheel is stopped.",
            },
            {
              k: "The machine",
              t: "Bowl and wheel",
              d: "The bowl is a window. The wheel is time. Elias built it to fetch a wife the door had already taken.",
            },
            {
              k: "The price",
              t: "Stay alone forever",
              d: "Stop the wheel. Sing forward. Portal closes. Kids stop dying. Someone remains on the rust.",
            },
          ].map((c) => (
            <article key={c.k} className="p-6 rounded-lg" style={{ border: '1px solid rgba(232, 223, 208, 0.1)', background: 'rgba(11, 10, 9, 0.4)' }}>
              <p className="text-xs tracking-[0.22em] uppercase" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-everlit)' }}>{c.k}</p>
              <h3 className="mt-2 text-xl" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-bone)' }}>{c.t}</h3>
              <p className="mt-3 text-sm" style={{ color: 'var(--dp-fog)' }}>{c.d}</p>
            </article>
          ))}
        </div>
      </section>

      <section style={{ borderTop: '1px solid rgba(232, 223, 208, 0.1)', background: 'var(--dp-soot)' }}>
        <div className="mx-auto max-w-5xl px-5 py-16 sm:px-8">
          <p className="text-xs tracking-[0.28em] uppercase" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-everlit)' }}>
            Theme song
          </p>
          <h2 className="mt-3 max-w-2xl text-3xl sm:text-4xl" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-bone)' }}>
            Some Songs Don't Take You to Neverland
          </h2>
          <p className="mt-3 max-w-2xl" style={{ color: 'var(--dp-paper)' }}>
            Official soundtrack. Neverland isn't a place you fly to. It's a job
            site you're sold on.
          </p>
          <div className="mt-8">
            <ThemePlayer />
          </div>
          <div className="mt-6">
            <Link to="/dark-pan/song" className="dp-btn dp-btn-ghost">Read the full lyric</Link>
          </div>
        </div>
      </section>

      {/* Navigation Links */}
      <section style={{ borderTop: '1px solid rgba(232, 223, 208, 0.1)', background: 'var(--dp-iron)' }}>
        <div className="mx-auto max-w-6xl px-5 py-12 sm:px-8">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <Link to="/dark-pan/story" className="block p-6 rounded-lg transition-all hover:scale-[1.02]" style={{ background: 'var(--dp-soot)', border: '1px solid rgba(232, 223, 208, 0.1)' }} data-testid="nav-story">
              <p className="text-xs tracking-[0.2em] uppercase" style={{ color: 'var(--dp-everlit)' }}>Read</p>
              <h3 className="mt-2 text-lg" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-bone)' }}>The Story</h3>
              <p className="mt-1 text-sm" style={{ color: 'var(--dp-fog)' }}>15 chapters across 5 acts</p>
            </Link>
            <Link to="/dark-pan/song" className="block p-6 rounded-lg transition-all hover:scale-[1.02]" style={{ background: 'var(--dp-soot)', border: '1px solid rgba(232, 223, 208, 0.1)' }} data-testid="nav-songs">
              <p className="text-xs tracking-[0.2em] uppercase" style={{ color: 'var(--dp-everlit)' }}>Listen</p>
              <h3 className="mt-2 text-lg" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-bone)' }}>Tinked</h3>
              <p className="mt-1 text-sm" style={{ color: 'var(--dp-fog)' }}>Songs & ABC lyrics</p>
            </Link>
            <Link to="/dark-pan/tones" className="block p-6 rounded-lg transition-all hover:scale-[1.02]" style={{ background: 'var(--dp-soot)', border: '1px solid rgba(232, 223, 208, 0.1)' }} data-testid="nav-tones">
              <p className="text-xs tracking-[0.2em] uppercase" style={{ color: 'var(--dp-everlit)' }}>Play</p>
              <h3 className="mt-2 text-lg" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-bone)' }}>Tones & Wheel</h3>
              <p className="mt-1 text-sm" style={{ color: 'var(--dp-fog)' }}>Y-U-O-I-E-A</p>
            </Link>
            <Link to="/dark-pan/worlds" className="block p-6 rounded-lg transition-all hover:scale-[1.02]" style={{ background: 'var(--dp-soot)', border: '1px solid rgba(232, 223, 208, 0.1)' }} data-testid="nav-worlds">
              <p className="text-xs tracking-[0.2em] uppercase" style={{ color: 'var(--dp-everlit)' }}>Explore</p>
              <h3 className="mt-2 text-lg" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-bone)' }}>The Worlds</h3>
              <p className="mt-1 text-sm" style={{ color: 'var(--dp-fog)' }}>Particle vs Wave</p>
            </Link>
            <Link to="/dark-pan/company" className="block p-6 rounded-lg transition-all hover:scale-[1.02]" style={{ background: 'var(--dp-soot)', border: '1px solid rgba(232, 223, 208, 0.1)' }} data-testid="nav-company">
              <p className="text-xs tracking-[0.2em] uppercase" style={{ color: 'var(--dp-everlit)' }}>Meet</p>
              <h3 className="mt-2 text-lg" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-bone)' }}>The Company</h3>
              <p className="mt-1 text-sm" style={{ color: 'var(--dp-fog)' }}>Pan, Hook, Tink, Mary</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Back to MGN link */}
      <div className="py-8 text-center" style={{ borderTop: '1px solid rgba(232, 223, 208, 0.1)' }}>
        <Link to="/" className="text-sm hover:opacity-80 transition-opacity" style={{ color: 'var(--dp-everlit)' }} data-testid="back-to-mgn">
          ← Back to MGN1.6
        </Link>
      </div>
    </Shell>
  );
}

// Story index page
function StoryIndex() {
  return (
    <Shell>
      <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8" data-testid="story-index">
        <Link to="/dark-pan" className="text-sm mb-8 inline-block" style={{ color: 'var(--dp-everlit)' }}>← Back to The Dark Pan</Link>
        <span className="mx-3 text-sm" style={{ color: 'var(--dp-fog)' }}>|</span>
        <Link to="/" className="text-sm mb-8 inline-block" style={{ color: 'var(--dp-everlit)' }}>← Back to MGN1.6</Link>
        
        <h1 className="text-4xl sm:text-5xl mb-4" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-bone)' }}>
          The Story
        </h1>
        <p className="text-lg mb-12" style={{ color: 'var(--dp-paper)' }}>
          Fifteen chapters across five acts. A cymatic fairy tale.
        </p>

        {ACTS.map((act) => (
          <div key={act.id} className="mb-12">
            <h2 className="text-sm tracking-[0.2em] uppercase mb-4" style={{ color: 'var(--dp-everlit)' }}>
              Act {act.id}: {act.title} <span style={{ color: 'var(--dp-fog)' }}>({act.span})</span>
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {CHAPTERS.filter(c => c.act === act.id).map((chapter) => (
                <Link 
                  key={chapter.id} 
                  to={`/dark-pan/story/${chapter.id}`}
                  className="block p-4 rounded-lg transition-all hover:scale-[1.02]"
                  style={{ background: 'var(--dp-iron)', border: '1px solid rgba(232, 223, 208, 0.1)' }}
                  data-testid={`chapter-${chapter.id}`}
                >
                  <img src={chapter.still} alt={chapter.title} className="w-full h-32 object-cover rounded-md mb-3" />
                  <p className="text-xs" style={{ color: 'var(--dp-fog)' }}>Chapter {chapter.number}</p>
                  <h3 className="text-lg" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-bone)' }}>{chapter.title}</h3>
                  <p className="text-sm mt-1" style={{ color: 'var(--dp-paper)' }}>{chapter.logline}</p>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Shell>
  );
}

// Chapter videos - 2 videos per chapter (add more as they become available)
const CHAPTER_VIDEOS = {
  'the-lullaby': [
    { id: 'Z2Nnm8xa5IA', title: 'Chapter 1 - Part 1', label: 'Chapter 1 · Part 1' },
    { id: 'fAI1acnTy1M', title: 'Chapter 1 - Part 2', label: 'Chapter 1 · Part 2' },
  ],
  'the-boy': [
    { id: 'nX2UScacpmw', title: '14 and 15 - Chapter 2', label: 'Chapter 2 · "14 and 15"' },
  ],
  'neverland-is-real': [
    { id: 'urrHbUhhfdY', title: '13 and Flat - Chapter 3, Part 1', label: 'Chapter 3 · Part 1 · "13 and Flat"' },
  ],
};

// Story chapter page
function StoryChapter() {
  const { id } = useParams();
  const chapter = getChapter(id);
  const { prev, next } = getAdjacent(id);
  const chapterVideos = CHAPTER_VIDEOS[id] || [];

  if (!chapter) {
    return (
      <Shell>
        <div className="mx-auto max-w-4xl px-5 py-16 text-center" data-testid="chapter-not-found">
          <h1 className="text-2xl" style={{ color: 'var(--dp-bone)' }}>Chapter not found</h1>
          <Link to="/dark-pan/story" className="dp-btn dp-btn-ghost mt-4">Back to story</Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="relative" data-testid={`chapter-page-${chapter.id}`}>
        <img src={chapter.still} alt={chapter.title} className="w-full h-64 sm:h-96 object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b0a09] via-[#0b0a09]/50 to-transparent" />
      </div>

      <div className="mx-auto max-w-3xl px-5 py-12 sm:px-8 -mt-24 relative z-10">
        <p className="text-xs tracking-[0.2em] uppercase" style={{ color: 'var(--dp-everlit)' }}>
          Chapter {chapter.number} · {chapter.location} · {chapter.year}
        </p>
        <h1 className="mt-3 text-3xl sm:text-4xl" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-bone)' }}>
          {chapter.title}
        </h1>
        <p className="mt-2 text-lg italic" style={{ color: 'var(--dp-paper)' }}>
          {chapter.logline}
        </p>

        {/* Chapter Videos Section - only shows if chapter has videos */}
        {chapterVideos.length > 0 && (
          <div className="mt-10 space-y-6" data-testid="chapter-videos">
            <h2 className="text-sm tracking-[0.2em] uppercase" style={{ color: 'var(--dp-everlit)' }}>Watch</h2>
            <div className={`grid gap-6 ${chapterVideos.length > 1 ? 'md:grid-cols-2' : ''}`}>
              {chapterVideos.map((video) => (
                <div key={video.id} data-testid={`chapter-video-${video.id}`}>
                  <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: 12, background: '#000' }}>
                    <iframe 
                      src={`https://www.youtube.com/embed/${video.id}?rel=0`}
                      title={video.title}
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      loading="lazy"
                    />
                  </div>
                  <p className="mt-2 text-sm" style={{ color: 'var(--dp-fog)' }}>
                    {video.label} · <a href={`https://youtu.be/${video.id}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--dp-everlit)' }} className="hover:underline">Watch on YouTube</a>
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 prose-film" style={{ color: 'var(--dp-paper)' }}>
          {chapter.body.map((p, i) => (
            <p key={`para-${chapter.id}-${i}`}>{p}</p>
          ))}
        </div>

        <div className="mt-8 p-4 rounded-lg" style={{ background: 'var(--dp-iron)', borderLeft: '3px solid var(--dp-everlit)' }}>
          <p className="text-sm italic" style={{ color: 'var(--dp-bone)' }}>{chapter.coda}</p>
        </div>

        <div className="mt-12 flex justify-between">
          {prev ? (
            <Link to={`/dark-pan/story/${prev.id}`} className="dp-btn dp-btn-ghost" data-testid="prev-chapter">← {prev.title}</Link>
          ) : <div />}
          {next ? (
            <Link to={`/dark-pan/story/${next.id}`} className="dp-btn dp-btn-ghost" data-testid="next-chapter">{next.title} →</Link>
          ) : <div />}
        </div>

        <div className="mt-8 text-center">
          <Link to="/dark-pan/story" className="text-sm" style={{ color: 'var(--dp-everlit)' }}>← All chapters</Link>
        </div>
      </div>
    </Shell>
  );
}

// Song page with ABC lyrics
function SongPage() {
  // Filter songs by category
  const trailerVideo = SONGS.find(s => s.id === 'trailer');
  const chapter1Video = SONGS.find(s => s.id === 'chapter1');
  const themeVideo = SONGS.find(s => s.id === 'theme');
  const abcVideo = SONGS.find(s => s.id === 'abc');

  const abcLyrics = [
    { letter: 'A', line: 'A is for Always, the promise he made.' },
    { letter: 'B', line: 'B is for Breath that the bowl gently takes.' },
    { letter: 'C', line: 'C is for Color that drains when we play.' },
    { letter: 'D', line: 'D is for Doorways that open in rain.' },
    { letter: 'E', line: 'E is for Echo, the sound of a name.' },
    { letter: 'F', line: 'F is for Feathers that fall when he fades.' },
    { letter: 'G', line: 'G is for Growing, the thing he won\'t do.' },
    { letter: 'H', line: 'H is for Hook, who remembers the truth.' },
    { letter: 'I', line: 'I is for Innocence stolen in two.' },
    { letter: 'J', line: 'J is for John, singing bright in the blue.' },
    { letter: 'K', line: 'K is for Kensington, myths that come through.' },
    { letter: 'L', line: 'L is for Lost Boys, who don\'t know they\'re used.' },
    { letter: 'M', line: 'M is for Mary, who sees both the frames.' },
    { letter: 'N', line: 'N is for Never, the cost of the game.' },
    { letter: 'O', line: 'O is for Open, the vowel that remains.' },
    { letter: 'P', line: 'P is for Pan, who is wave and is pain.' },
    { letter: 'Q', line: 'Q is for Quiet, the silence that came.' },
    { letter: 'R', line: 'R is for Resonance, humming the same.' },
    { letter: 'S', line: 'S is for Sand, forming stars in the bowl.' },
    { letter: 'T', line: 'T is for Tink, keeping love on control.' },
    { letter: 'U', line: 'U is for Under, the world that he stole.' },
    { letter: 'V', line: 'V is for Vapor, the Everlit glow.' },
    { letter: 'W', line: 'W is Workhouse, where shadows still go.' },
    { letter: 'X', line: 'X is the Crossing, where two versions show.' },
    { letter: 'Y', line: 'Y is the Yielding, the backward tone\'s plea.' },
    { letter: 'Z', line: 'Z is the Zero, the end of the dream.' },
  ];

  const verses = [
    {
      title: 'Verse',
      kicker: 'The night-shift map',
      lines: [
        "Second star to the right and straight on till morning,",
        "That's not a map, that's a night shift warning.",
        "London 1904, cold brick, pram fell,",
        "Boy didn't die, he just fell out of the story they sell."
      ]
    },
    {
      title: 'First',
      kicker: 'Wendy / Darling House — the original context',
      lines: [
        "Wendy Darling, attic, mother sewing late,",
        "Two brothers in one bed, father checking the rate.",
        "Dog for a nanny 'cause a woman costs more,",
        "Peter at the window says you'll never keep score.",
        "Neverland. No school. No time. No pain.",
        "Lost Boys who fell out of prams when the nanny looked away.",
        "Tinker Bell, brass bowl in her hand, not a light,",
        "Jealous? No. She built the whole thing that night."
      ]
    },
    {
      title: 'Chorus',
      kicker: 'Standalone Pan, twist coded in',
      lines: [
        "Peter Pan don't wanna work,",
        "So he built a world where play is work.",
        "Tink tinkers, bowl hums, sand draws the fair,",
        "Hook waits below with the same face, same hair.",
        "Lost Boys sing for the light in the air,",
        "Yellow, blue, red, green — rent for Neverland,",
        "Second star looks free till you understand,",
        "You pay for it in time you never had."
      ]
    },
    {
      title: 'Verse 2',
      kicker: 'The social truth in Barrie, and in ours',
      lines: [
        "Barrie knew: Lost Boys are orphans, that's not cute,",
        "That's 1904, match factory, workhouse boot.",
        "They fall out of prams — moms working mills, can't afford the keep,",
        "They don't die, they get unmapped, swept off the street.",
        "Peter's the foreman who sells you eternal youth,",
        "You never have to grow up — oldest company truth.",
        "Hook is grown Pan — what happens when you DO grow,",
        "Same boy, weathered, captain coat, hand cut trying to let go.",
        "Hook's not pirate, he's police of the other side,",
        "Time-clock with a crocodile that swallows time.",
        "Tink's bowl is cymatic — she sings 7 cents flat,",
        "Vibrates sand into ferris wheel, carousel, all that.",
        "Disney left side, Sin City right,",
        "She holds both in a brass bowl every night."
      ]
    },
    {
      title: 'Bridge',
      kicker: 'The return',
      lines: [
        "Wendy closed the window, not because she had to,",
        "Because she wanted breakfast, winter, something true.",
        "In our film, the girl does the same at dawn,",
        "Bowl in river, no ripple, Neverland gone.",
        "No second star, just Battersea swamp, rust,",
        "Ferris wheel standing, but covered in dust.",
        "Lost Boys don't fly home, they walk, one by one,",
        "Learning how to hold a spoon, not a gun.",
        "Pan don't die fighting Hook. He sits with him.",
        "Same coat now. Same face. Both thin."
      ]
    },
    {
      title: 'Whisper',
      kicker: 'Tinked and un-tinked',
      lines: [
        "Tinked and un-tinked,",
        "Let the sand go flat.",
        "Let the boy be a man,",
        "And let that be that."
      ]
    },
    {
      title: 'Final chorus',
      kicker: 'You were always the power',
      lines: [
        "Peter Pan didn't want to work,",
        "So he built a world where work don't work.",
        "Tink built the world in a singing bowl,",
        "With second star inside, for a toll.",
        "Hook waited below with the same face, same soul,",
        "Captain, ranger, time just took its toll.",
        "Lost Boys paid for the light in the air,",
        "Yellow, blue, red, green — rent for Neverland.",
        "Second star looked free till you understand —",
        "You were always the power for Neverland.",
        "Second star to the right…",
        "…is still a job site if you don't turn out the light."
      ]
    }
  ];

  return (
    <Shell>
      <div className="mx-auto max-w-5xl px-5 py-16 sm:px-8" data-testid="songs-page">
        <Link to="/dark-pan" className="text-sm mb-8 inline-block" style={{ color: 'var(--dp-everlit)' }}>← Back to The Dark Pan</Link>
        <span className="mx-3 text-sm" style={{ color: 'var(--dp-fog)' }}>|</span>
        <Link to="/" className="text-sm mb-8 inline-block" style={{ color: 'var(--dp-everlit)' }}>← Back to MGN1.6</Link>

        <p className="text-xs tracking-[0.28em] uppercase mb-2" style={{ color: 'var(--dp-everlit)' }}>WTFairytale · The Dark Pan</p>
        <h1 className="text-4xl sm:text-5xl mb-4" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-bone)' }}>
          Some Songs Don't Take You to Neverland
        </h1>
        <p className="text-lg mb-4" style={{ color: 'var(--dp-paper)' }}>
          Neverland isn't a place you fly to. It's a job site you're sold on. Second star to the right is a night shift.
        </p>

        {/* Trailer */}
        {trailerVideo && (
          <div className="mb-10">
            <p className="text-sm mb-3" style={{ color: 'var(--dp-fog)' }}>Trailer · <a href={`https://youtu.be/${trailerVideo.youtubeId}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--dp-everlit)' }} className="hover:underline">youtu.be/{trailerVideo.youtubeId}</a></p>
          </div>
        )}

        {/* Chapter 1 and Theme side by side */}
        <div className="grid gap-6 md:grid-cols-2 mb-8">
          {chapter1Video && (
            <div data-testid="song-chapter1">
              <div className="relative w-full overflow-hidden rounded-xl bg-black aspect-video">
                <iframe 
                  src={`https://www.youtube.com/embed/${chapter1Video.youtubeId}?rel=0`}
                  title="Chapter 1"
                  className="absolute inset-0 h-full w-full border-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              <p className="mt-3 text-sm" style={{ color: 'var(--dp-fog)' }}>Chapter 1 · <a href={`https://youtu.be/${chapter1Video.youtubeId}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--dp-everlit)' }} className="hover:underline">youtu.be/{chapter1Video.youtubeId}</a></p>
            </div>
          )}
          {themeVideo && (
            <div data-testid="song-theme">
              <div className="relative w-full overflow-hidden rounded-xl bg-black aspect-video">
                <iframe 
                  src={`https://www.youtube.com/embed/${themeVideo.youtubeId}?rel=0`}
                  title="Theme"
                  className="absolute inset-0 h-full w-full border-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              <p className="mt-3 text-sm" style={{ color: 'var(--dp-fog)' }}>Theme · <a href={`https://youtu.be/${themeVideo.youtubeId}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--dp-everlit)' }} className="hover:underline">youtu.be/{themeVideo.youtubeId}</a></p>
            </div>
          )}
        </div>

        {/* ABC Video - Full Width */}
        {abcVideo && (
          <div className="mb-16" data-testid="song-abc">
            <div className="relative w-full overflow-hidden rounded-xl bg-black aspect-video">
              <iframe 
                src={`https://www.youtube.com/embed/${abcVideo.youtubeId}?rel=0`}
                title="ABC song"
                className="absolute inset-0 h-full w-full border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
              />
            </div>
            <p className="mt-3 text-sm" style={{ color: 'var(--dp-fog)' }}>ABC · <a href={`https://youtu.be/${abcVideo.youtubeId}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--dp-everlit)' }} className="hover:underline">youtu.be/{abcVideo.youtubeId}</a></p>
          </div>
        )}

        {/* ABC Lyrics Section */}
        <h2 className="text-2xl sm:text-3xl mb-6" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-bone)' }}>
          The ABC
        </h2>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 mb-16" data-testid="abc-lyrics">
          {abcLyrics.map((item) => (
            <div 
              key={item.letter} 
              className="p-3 rounded-lg flex items-start gap-3"
              style={{ background: 'var(--dp-iron)', border: '1px solid rgba(232, 223, 208, 0.1)' }}
            >
              <span className="text-2xl font-bold" style={{ color: 'var(--dp-everlit)', fontFamily: 'var(--dp-font-display)' }}>{item.letter}</span>
              <span className="text-sm" style={{ color: 'var(--dp-paper)' }}>{item.line}</span>
            </div>
          ))}
        </div>

        {/* Full Lyrics */}
        <h2 className="text-2xl sm:text-3xl mb-6" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-bone)' }}>
          Full Lyrics
        </h2>
        <div className="space-y-8" data-testid="full-lyrics">
          {verses.map((verse, idx) => (
            <article key={idx} className="p-6 rounded-xl" style={{ background: 'var(--dp-iron)', border: '1px solid rgba(232, 223, 208, 0.1)' }}>
              <p className="text-xs tracking-[0.2em] uppercase mb-2" style={{ color: 'var(--dp-everlit)' }}>{verse.kicker}</p>
              <h3 className="text-xl mb-4" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-bone)' }}>{verse.title}</h3>
              <div className="space-y-2">
                {verse.lines.map((line, i) => (
                  <p key={i} style={{ color: 'var(--dp-paper)' }}>{line}</p>
                ))}
              </div>
            </article>
          ))}
        </div>
      </div>
    </Shell>
  );
}

// Tones page with interactive bowl, Merkaba, and spinning wheel
function TonesPage() {
  const centsFlatMultiplier = Math.pow(2, -7/1200);
  
  const tones = [
    { note: 'Y', meaning: 'I yield', freq: 493.88 * centsFlatMultiplier, angle: 0 },
    { note: 'U', meaning: 'You return', freq: 440.00 * centsFlatMultiplier, angle: 60 },
    { note: 'O', meaning: 'We are one', freq: 392.00 * centsFlatMultiplier, angle: 120 },
    { note: 'I', meaning: 'I remember', freq: 329.63 * centsFlatMultiplier, angle: 180 },
    { note: 'E', meaning: 'We end', freq: 293.66 * centsFlatMultiplier, angle: 240 },
    { note: 'A', meaning: 'Amen', freq: 220.00 * centsFlatMultiplier, angle: 300 },
  ];

  const [activeNote, setActiveNote] = React.useState(null);
  const [wheelDirection, setWheelDirection] = React.useState(null);
  const [wheelRotation, setWheelRotation] = React.useState(0);
  const [sandResult, setSandResult] = React.useState(null);
  const [sequence, setSequence] = React.useState([]);
  const [merkabaActive, setMerkabaActive] = React.useState(false);
  const audioContextRef = React.useRef(null);
  const wheelIntervalRef = React.useRef(null);

  const spinForward = () => {
    setWheelDirection('forward');
    setSandResult(null);
    setMerkabaActive(false);
    if (wheelIntervalRef.current) clearInterval(wheelIntervalRef.current);
    wheelIntervalRef.current = setInterval(() => {
      setWheelRotation(r => r + 3);
    }, 16);
  };

  const spinBackward = () => {
    setWheelDirection('backward');
    setSandResult(null);
    setMerkabaActive(false);
    if (wheelIntervalRef.current) clearInterval(wheelIntervalRef.current);
    wheelIntervalRef.current = setInterval(() => {
      setWheelRotation(r => r - 3);
    }, 16);
  };

  const stopWheel = () => {
    setWheelDirection('stopped');
    if (wheelIntervalRef.current) {
      clearInterval(wheelIntervalRef.current);
      wheelIntervalRef.current = null;
    }
  };

  React.useEffect(() => {
    if (sequence.length >= 6 && wheelDirection) {
      const backward = ['Y', 'U', 'O', 'I', 'E', 'A'];
      const forward = ['A', 'E', 'I', 'O', 'U', 'Y'];
      const seqStr = sequence.slice(-6).join('');
      
      if (wheelDirection === 'forward' && seqStr === backward.join('')) {
        setSandResult('FEED');
        setMerkabaActive(true);
      } else if (wheelDirection === 'backward' && seqStr === backward.join('')) {
        setSandResult('SACRIFICE');
        setMerkabaActive(true);
      } else if (wheelDirection === 'stopped' && seqStr === forward.join('')) {
        setSandResult('WINDOW');
        setMerkabaActive(true);
      }
    }
  }, [sequence, wheelDirection]);

  const playTone = (freq, note) => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const ctx = audioContextRef.current;
    if (ctx.state === 'suspended') ctx.resume();

    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(freq, ctx.currentTime);
    
    const vibrato = ctx.createOscillator();
    const vibratoGain = ctx.createGain();
    vibrato.frequency.setValueAtTime(5, ctx.currentTime);
    vibratoGain.gain.setValueAtTime(2, ctx.currentTime);
    vibrato.connect(vibratoGain);
    vibratoGain.connect(oscillator.frequency);
    vibrato.start();
    
    gainNode.gain.setValueAtTime(0, ctx.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.4, ctx.currentTime + 0.05);
    gainNode.gain.linearRampToValueAtTime(0.3, ctx.currentTime + 0.2);
    gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + 1.2);
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 1.2);
    
    setActiveNote(note);
    setSequence(s => [...s, note]);
    setTimeout(() => setActiveNote(null), 1200);
    
    oscillator.onended = () => {
      vibrato.stop();
      oscillator.disconnect();
      gainNode.disconnect();
    };
  };

  React.useEffect(() => {
    return () => {
      if (wheelIntervalRef.current) clearInterval(wheelIntervalRef.current);
    };
  }, []);

  // Merkaba SVG component
  const MerkabaSVG = ({ active, rotation }) => (
    <svg viewBox="0 0 200 200" className="w-full h-full" style={{ transform: `rotate(${rotation}deg)` }}>
      <defs>
        <radialGradient id="sandGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={active ? "#C9A227" : "#8B7355"} stopOpacity={active ? "0.8" : "0.3"} />
          <stop offset="100%" stopColor="#0A0605" stopOpacity="0" />
        </radialGradient>
        <filter id="glow">
          <feGaussianBlur stdDeviation={active ? "3" : "1"} result="coloredBlur"/>
          <feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>
      
      {/* Bowl rim */}
      <ellipse cx="100" cy="100" rx="90" ry="90" fill="none" stroke="#3d3428" strokeWidth="4" />
      <ellipse cx="100" cy="100" rx="85" ry="85" fill="none" stroke="#2a231a" strokeWidth="2" />
      <circle cx="100" cy="100" r="80" fill="url(#sandGlow)" />
      
      {/* Merkaba geometry */}
      <g filter="url(#glow)" style={{ opacity: active ? 1 : 0.2, transition: 'opacity 0.5s' }}>
        <polygon points="100,30 160,140 40,140" fill="none" stroke={active ? "#C9A227" : "#5a4d3a"} strokeWidth={active ? "2" : "1"} />
        <polygon points="100,170 40,60 160,60" fill="none" stroke={active ? "#8EC8D8" : "#3a4d5a"} strokeWidth={active ? "2" : "1"} />
        <polygon points="100,50 145,77 145,123 100,150 55,123 55,77" fill="none" stroke={active ? "#C9A227" : "#4a4030"} strokeWidth="1" strokeDasharray={active ? "0" : "4,4"} />
        <circle cx="100" cy="100" r={active ? "8" : "4"} fill={active ? "#C9A227" : "#5a4d3a"} />
      </g>
      
      {/* Sand particles */}
      {!active && [...Array(30)].map((_, i) => (
        <circle key={i} cx={100 + Math.cos(i * 0.7 + rotation * 0.01) * (30 + (i % 5) * 10)} cy={100 + Math.sin(i * 0.7 + rotation * 0.01) * (30 + (i % 5) * 10)} r="1.5" fill="#8B7355" opacity="0.6" />
      ))}
    </svg>
  );

  return (
    <Shell>
      <div className="mx-auto max-w-5xl px-4 py-6" data-testid="tones-page">
        <Link to="/dark-pan" className="text-sm mb-4 inline-block" style={{ color: 'var(--dp-everlit)' }}>← Back</Link>

        <div className="text-center mb-4">
          <h1 className="text-2xl sm:text-3xl" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-bone)' }}>The Six Tones</h1>
          <p className="text-sm" style={{ color: 'var(--dp-fog)' }}>Spin the bowl. Sing the tones. The sand forms a Merkaba.</p>
        </div>

        {/* Main Bowl with Tones around it */}
        <div className="relative mx-auto" style={{ maxWidth: 420, aspectRatio: '1' }}>
          {/* The Bowl with Merkaba */}
          <div className="absolute inset-6 rounded-full overflow-hidden" style={{ background: 'radial-gradient(circle, #1a1510 0%, #0A0605 100%)', boxShadow: merkabaActive ? '0 0 60px rgba(201, 162, 39, 0.5), inset 0 0 40px rgba(201, 162, 39, 0.2)' : 'inset 0 0 30px rgba(0,0,0,0.8)', transition: 'box-shadow 0.5s' }}>
            <MerkabaSVG active={merkabaActive} rotation={wheelRotation} />
          </div>

          {/* Tone buttons around the bowl */}
          {tones.map((tone) => {
            const angle = (tone.angle - 90) * (Math.PI / 180);
            const radius = 47;
            const x = 50 + radius * Math.cos(angle);
            const y = 50 + radius * Math.sin(angle);
            
            return (
              <button
                key={tone.note}
                onClick={() => playTone(tone.freq, tone.note)}
                className={`absolute w-11 h-11 rounded-full flex items-center justify-center text-base font-bold transition-all duration-200 ${activeNote === tone.note ? 'scale-125 z-10' : 'hover:scale-110'}`}
                style={{ left: `${x}%`, top: `${y}%`, transform: 'translate(-50%, -50%)', background: activeNote === tone.note ? '#fff' : 'var(--dp-everlit)', color: 'var(--dp-soot)', boxShadow: activeNote === tone.note ? '0 0 25px rgba(142, 200, 216, 0.8)' : '0 2px 6px rgba(0,0,0,0.3)', fontFamily: 'var(--dp-font-display)' }}
                data-testid={`tone-${tone.note}`}
              >
                {tone.note}
              </button>
            );
          })}

          {/* Center result display */}
          {sandResult && (
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none z-10">
              <div className="text-xl font-bold animate-pulse" style={{ color: '#C9A227', fontFamily: 'var(--dp-font-display)', textShadow: '0 0 20px rgba(201, 162, 39, 0.8)' }}>{sandResult}</div>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="flex justify-center gap-2 mt-2">
          <button onClick={spinBackward} className={`px-3 py-1.5 rounded-lg text-sm font-bold transition-all ${wheelDirection === 'backward' ? 'ring-2 ring-white scale-105' : ''}`} style={{ background: 'var(--dp-everlit)', color: 'var(--dp-soot)' }}>← Bwd</button>
          <button onClick={stopWheel} className={`px-3 py-1.5 rounded-lg text-sm font-bold transition-all ${wheelDirection === 'stopped' ? 'ring-2 ring-white scale-105' : ''}`} style={{ background: '#C9A227', color: 'var(--dp-soot)' }}>■ Stop</button>
          <button onClick={spinForward} className={`px-3 py-1.5 rounded-lg text-sm font-bold transition-all ${wheelDirection === 'forward' ? 'ring-2 ring-white scale-105' : ''}`} style={{ background: 'var(--dp-everlit)', color: 'var(--dp-soot)' }}>Fwd →</button>
        </div>

        {/* Sequence & Instructions */}
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <div className="p-3 rounded-xl" style={{ background: 'var(--dp-iron)', border: '1px solid rgba(232, 223, 208, 0.1)' }}>
            <p className="text-xs tracking-wider uppercase mb-1" style={{ color: 'var(--dp-fog)' }}>Your Sequence</p>
            <p className="text-lg font-mono" style={{ color: 'var(--dp-everlit)' }}>{sequence.slice(-6).join(' - ') || '...'}</p>
          </div>
          <div className="p-3 rounded-xl" style={{ background: 'var(--dp-iron)', border: '1px solid rgba(232, 223, 208, 0.1)' }}>
            <p className="text-xs tracking-wider uppercase mb-1" style={{ color: 'var(--dp-fog)' }}>Sand Names</p>
            <div className="flex flex-wrap gap-2 text-xs" style={{ color: 'var(--dp-paper)' }}>
              <span><strong style={{ color: '#C9A227' }}>FEED</strong>=Fwd+YUOIEA</span>
              <span><strong style={{ color: '#C9A227' }}>SACRIFICE</strong>=Bwd+YUOIEA</span>
              <span><strong style={{ color: '#C9A227' }}>WINDOW</strong>=Stop+AEIOUY</span>
            </div>
          </div>
        </div>

        <div className="mt-4 p-3 rounded-xl text-center" style={{ background: 'rgba(142, 200, 216, 0.1)', border: '1px solid var(--dp-everlit)' }}>
          <p className="text-base" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-everlit)' }}>The thirteenth tone is silence, chosen.</p>
        </div>
      </div>
    </Shell>
  );
}

// Company page
function CompanyPage() {
  return (
    <Shell>
      <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8" data-testid="company-page">
        <Link to="/dark-pan" className="text-sm mb-8 inline-block" style={{ color: 'var(--dp-everlit)' }}>← Back to The Dark Pan</Link>
        <span className="mx-3 text-sm" style={{ color: 'var(--dp-fog)' }}>|</span>
        <Link to="/" className="text-sm mb-8 inline-block" style={{ color: 'var(--dp-everlit)' }}>← Back to MGN1.6</Link>

        <p className="text-xs tracking-[0.28em] uppercase" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-everlit)' }}>
          Same face, different clothes
        </p>
        <h1 className="mt-3 text-4xl sm:text-5xl mb-4" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-bone)' }}>
          The Company
        </h1>
        <p className="text-lg mb-12" style={{ color: 'var(--dp-paper)' }}>
          Pan fighting Hook is a boy fighting his own future. Tink is not jealous.
          She is a tinkerer who could not sing amen.
        </p>

        <div className="space-y-16">
          {CHARACTERS.map((character, i) => (
            <article
              key={character.id}
              className="grid items-start gap-8 border-t pt-10 lg:grid-cols-2"
              style={{ borderColor: 'rgba(232, 223, 208, 0.1)' }}
              data-testid={`character-${character.id}`}
            >
              <img
                src={character.still}
                alt={character.name}
                className={`h-80 w-full rounded-xl object-cover sm:h-[28rem] ${i % 2 === 1 ? "lg:order-2" : ""}`}
                style={{ objectPosition: '50% 20%' }}
              />
              <div className={i % 2 === 1 ? "lg:order-1" : ""}>
                <p className="text-xs tracking-[0.22em] uppercase" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-everlit)' }}>
                  {character.role} · {character.age}
                </p>
                <h2 className="mt-2 text-3xl" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-bone)' }}>{character.name}</h2>
                <div className="mt-5 prose-film" style={{ color: 'var(--dp-paper)' }}>
                  {character.bio.map((p, idx) => (
                    <p key={idx}>{p}</p>
                  ))}
                </div>
                
                {/* Tink's Video + Song */}
                {character.id === 'tink' && (
                  <div className="mt-6" data-testid="tink-video">
                    <p className="text-xs tracking-[0.15em] uppercase mb-3" style={{ color: 'var(--dp-everlit)' }}>Watch Tink</p>
                    <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: 12, background: '#000' }}>
                      <iframe
                        src="https://www.youtube.com/embed/7OeIsCnpcP0?rel=0"
                        title="Tink Tinker Pan | The Dark Pan Saga"
                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        loading="lazy"
                      />
                    </div>
                    <p className="mt-2 text-sm" style={{ color: 'var(--dp-fog)' }}>
                      <a href="https://youtu.be/7OeIsCnpcP0" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--dp-everlit)' }} className="hover:underline">Watch on YouTube</a>
                    </p>
                  </div>
                )}

                {/* Tink's Song - Audio Player */}
                {character.id === 'tink' && (
                  <div className="mt-6 p-4 rounded-lg" style={{ background: 'var(--dp-iron)', border: '1px solid rgba(201, 162, 39, 0.3)' }}>
                    <p className="text-xs tracking-[0.15em] uppercase mb-3" style={{ color: 'var(--dp-everlit)' }}>Listen to Tink's Song</p>
                    <audio 
                      controls 
                      className="w-full"
                      style={{ filter: 'sepia(20%) saturate(70%) hue-rotate(20deg)' }}
                      data-testid="tink-song-audio"
                    >
                      <source src="https://customer-assets-jai6qajn.emergentagent.net/job_author-hub-59/artifacts/zgdlvmmd_Tink_Is_a_Verb.wav" type="audio/wav" />
                      Your browser does not support the audio element.
                    </audio>
                    <p className="mt-2 text-sm italic" style={{ color: 'var(--dp-fog)' }}>
                      "Tink Is a Verb" — Ti lascio andare. I let you go.
                    </p>
                  </div>
                )}
                
                {character.gallery && (
                  <ul className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {character.gallery.map((src) => (
                      <li key={src}>
                        <img
                          src={src}
                          alt=""
                          className="h-24 w-full rounded-md object-cover sm:h-28"
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </Shell>
  );
}

// Worlds page - Particle vs Wave
function WorldsPage() {
  const worlds = [
    {
      id: 'particle',
      name: 'World A — Particle',
      subtitle: 'The Factory',
      color: '#4a4a4a',
      image: '/stills/workhouse.jpg',
      description: 'Black and white. Sin City palette. London 1892. Match factory. Workhouse. The real world where children labor.',
      rules: [
        'Weight is constant',
        'Time moves forward',
        'Blood is red, then brown',
        'Death is permanent',
        'You age whether you want to or not'
      ]
    },
    {
      id: 'wave',
      name: 'World B — Wave',
      subtitle: 'Neverland',
      color: '#8EC8D8',
      image: '/stills/neverland.jpg',
      description: 'Oversaturated color. Disney palette. The abandoned World\'s Fair in Battersea swamp. Eternal youth at a cost.',
      rules: [
        'Weight is negotiable',
        'Time is a wheel you can spin',
        'Blood is Everlit blue vapor',
        'Death is just a door',
        'You never grow up — until you do'
      ]
    },
    {
      id: 'merged',
      name: 'World C — Merged',
      subtitle: 'The Crossing',
      color: '#C9A227',
      image: '/stills/split.jpg',
      description: 'Both palettes visible. The moment when the two films become one. Where Mary stands with coat in color, shadow in noir.',
      rules: [
        'Particle and wave occupy same space',
        'Same face, different ages',
        'Causality tax applies',
        'The wheel can be stopped',
        'Forward closes permanently'
      ]
    },
    {
      id: 'honest',
      name: 'World D — Honest',
      subtitle: 'After',
      color: '#B7A99A',
      image: '/stills/ending.jpg',
      description: 'One film stock. Desaturated. Late afternoon light that is actually real. Not noir, not Disney. Just London.',
      rules: [
        'The wheel never moves again',
        'Fifteen boys remember both',
        'They play after work',
        'They come home',
        'Not Lost. Found.'
      ]
    }
  ];

  return (
    <Shell>
      <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8" data-testid="worlds-page">
        <Link to="/dark-pan" className="text-sm mb-8 inline-block" style={{ color: 'var(--dp-everlit)' }}>← Back to The Dark Pan</Link>
        <span className="mx-3 text-sm" style={{ color: 'var(--dp-fog)' }}>|</span>
        <Link to="/" className="text-sm mb-8 inline-block" style={{ color: 'var(--dp-everlit)' }}>← Back to MGN1.6</Link>

        <p className="text-xs tracking-[0.28em] uppercase mb-2" style={{ color: 'var(--dp-everlit)' }}>Four states of being</p>
        <h1 className="text-4xl sm:text-5xl mb-4" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-bone)' }}>
          The Worlds
        </h1>
        <p className="text-lg mb-12 max-w-2xl" style={{ color: 'var(--dp-paper)' }}>
          We fabricated two timelines to tell one story. Disney left. Sin City right. A brass bowl in the middle.
        </p>

        <div className="space-y-12">
          {worlds.map((world, idx) => (
            <article 
              key={world.id}
              className={`grid gap-8 lg:grid-cols-2 items-center ${idx % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
              data-testid={`world-${world.id}`}
            >
              <div className={idx % 2 === 1 ? 'lg:order-2' : ''}>
                <img 
                  src={world.image} 
                  alt={world.name}
                  className="w-full h-64 sm:h-80 object-cover rounded-xl"
                />
              </div>
              <div className={idx % 2 === 1 ? 'lg:order-1' : ''}>
                <div 
                  className="w-4 h-4 rounded-full mb-4"
                  style={{ background: world.color }}
                />
                <p className="text-xs tracking-[0.2em] uppercase mb-2" style={{ color: 'var(--dp-everlit)' }}>{world.subtitle}</p>
                <h2 className="text-2xl sm:text-3xl mb-4" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-bone)' }}>
                  {world.name}
                </h2>
                <p className="mb-6" style={{ color: 'var(--dp-paper)' }}>
                  {world.description}
                </p>
                <ul className="space-y-2">
                  {world.rules.map((rule, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm" style={{ color: 'var(--dp-fog)' }}>
                      <span style={{ color: world.color }}>●</span>
                      {rule}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-16 p-8 rounded-xl text-center" style={{ background: 'var(--dp-iron)', border: '1px solid rgba(232, 223, 208, 0.1)' }}>
          <p className="text-xl mb-4" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-bone)' }}>
            "Pan fighting Hook is a boy fighting his own future."
          </p>
          <p style={{ color: 'var(--dp-fog)' }}>
            Same face. Different clothes. Wave and particle. Twenty years of suffering apart.
          </p>
        </div>
      </div>
    </Shell>
  );
}

// Main routes component
export default function DarkPanRoutes() {
  return (
    <Routes>
      <Route index element={<DarkPanHome />} />
      <Route path="story" element={<StoryIndex />} />
      <Route path="story/:id" element={<StoryChapter />} />
      <Route path="song" element={<SongPage />} />
      <Route path="lyrics" element={<LyricsPage />} />
      <Route path="tones" element={<TonesPage />} />
      <Route path="abc" element={<AbcPage />} />
      <Route path="worlds" element={<WorldsPage />} />
      <Route path="company" element={<CompanyPage />} />
    </Routes>
  );
}
